import { useState } from 'react';
import { Loader2, AlertTriangle, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';

const BUCKET_META = {
  confirmed: {
    label: 'Confirmed (recoverable)',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    desc: 'Direct supplier, payee + currency verified',
  },
  aggregator_batch: {
    label: 'Aggregator (batch)',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    desc: 'OTA platform supplier, batch reconciliation only',
  },
  unconfirmed: {
    label: 'Unconfirmed (direct)',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    desc: 'Direct supplier, payee mismatch or no Wise match',
  },
  no_supplier_on_property: {
    label: 'No supplier on property',
    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    desc: 'Property missing supplier_id/supplier_name (data gap)',
  },
  no_candidate: {
    label: 'No candidate',
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    desc: 'No outbound Wise payment in range',
  },
};

const BUCKET_ORDER = [
  'confirmed',
  'aggregator_batch',
  'unconfirmed',
  'no_supplier_on_property',
  'no_candidate',
];

function fmt(n) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

function BucketSummary({ bucketKey, bucket }) {
  const meta = BUCKET_META[bucketKey];
  if (!meta || !bucket) return null;
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2">
        <Badge className={meta.color}>{meta.label}</Badge>
        <span className="text-xs text-muted-foreground">{meta.desc}</span>
      </div>
      <div className="text-sm font-mono text-right">
        {bucket.count} &middot; {fmt(bucket.total_gbp)}
      </div>
    </div>
  );
}

function BookingTable({ rows, bucketKey }) {
  const [open, setOpen] = useState(false);
  if (!rows || rows.length === 0) return null;
  const meta = BUCKET_META[bucketKey];
  if (!meta) return null;

  const showPayeeColumns = bucketKey === 'confirmed' || bucketKey === 'unconfirmed';
  const showPropertyId = bucketKey === 'no_supplier_on_property';

  return (
    <div className="border rounded-md">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 w-full px-3 py-1.5 text-xs font-medium text-left hover:bg-muted/50"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {meta.label} ({rows.length})
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-t bg-muted/30">
                <th className="px-2 py-1 text-left font-medium">Booking</th>
                {showPropertyId && (
                  <th className="px-2 py-1 text-left font-medium">Property</th>
                )}
                <th className="px-2 py-1 text-left font-medium">Supplier (property)</th>
                {showPayeeColumns && (
                  <>
                    <th className="px-2 py-1 text-left font-medium">Wise payee</th>
                    <th className="px-2 py-1 text-center font-medium">Payee?</th>
                    <th className="px-2 py-1 text-center font-medium">CCY?</th>
                  </>
                )}
                <th className="px-2 py-1 text-right font-medium">Supplier Total</th>
                <th className="px-2 py-1 text-left font-medium">Check-in</th>
                <th className="px-2 py-1 text-left font-medium">Match Basis</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.booking_id} className="border-t">
                  <td className="px-2 py-1 font-mono">{r.booking_id}</td>
                  {showPropertyId && (
                    <td className="px-2 py-1 font-mono text-xs">{r.property_id || '-'}</td>
                  )}
                  <td className="px-2 py-1">{r.supplier_name || r.property_name || '-'}</td>
                  {showPayeeColumns && (
                    <>
                      <td className="px-2 py-1">{r.wise_payee || '-'}</td>
                      <td className="px-2 py-1 text-center">
                        {r.payee_verified === true
                          ? <span className="text-green-600 dark:text-green-400">Yes</span>
                          : r.payee_verified === false
                            ? <span className="text-red-500">No</span>
                            : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {r.currency_verified === true
                          ? <span className="text-green-600 dark:text-green-400">Yes</span>
                          : r.currency_verified === false
                            ? <span className="text-red-500">No</span>
                            : <span className="text-gray-400">-</span>}
                      </td>
                    </>
                  )}
                  <td className="px-2 py-1 text-right font-mono">{fmt(r.supplier_total)}</td>
                  <td className="px-2 py-1">{r.check_in || '-'}</td>
                  <td className="px-2 py-1">{r.match_basis || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SupplierPaymentMatchPreviewPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.supplierPaymentMatchPreview();
      setResult(data);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        'Request failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Supplier Payment Match Preview</CardTitle>
          <Button variant="outline" size="sm" onClick={runCheck} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!result && !error && !loading && (
          <p className="text-sm text-muted-foreground">
            Press Check to measure how many unlinked BR OS bookings resolve to a Wise outbound payment.
          </p>
        )}

        {error && (
          <div className="flex items-start gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            {/* Headline: confirmed-direct only */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {result.total_unlinked_bookings} unlinked booking{result.total_unlinked_bookings !== 1 ? 's' : ''}
              </span>
              <div className="text-right">
                <div className="text-xl font-bold font-mono">{fmt(result.confirmed_total_gbp)}</div>
                <div className="text-xs text-muted-foreground">confirmed recoverable (direct, payee-verified)</div>
              </div>
            </div>

            {/* Zero confirmed explanation */}
            {result.confirmed_total_gbp === 0 && (
              <div className="rounded-md border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2 text-xs text-yellow-800 dark:text-yellow-300">
                No matches could be confirmed against a supplier payee; booking records lack supplier identity.
              </div>
            )}

            {/* Secondary: unconfirmed (not recoverable) */}
            {result.buckets.unconfirmed && result.buckets.unconfirmed.total_gbp > 0 && (
              <div className="rounded-md border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-yellow-800 dark:text-yellow-300">
                    Unconfirmed direct (amount+date only, needs manual review)
                  </span>
                  <span className="text-sm font-mono font-semibold text-yellow-800 dark:text-yellow-300">
                    {result.buckets.unconfirmed.count} &middot; {fmt(result.buckets.unconfirmed.total_gbp)}
                  </span>
                </div>
                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                  These matches lack payee or currency verification and are NOT counted as recoverable.
                </p>
              </div>
            )}

            {/* Tier breakdown */}
            <div className="border rounded-md px-3 py-1 divide-y">
              {BUCKET_ORDER.map((k) => (
                result.buckets[k] && <BucketSummary key={k} bucketKey={k} bucket={result.buckets[k]} />
              ))}
            </div>

            {/* No-supplier property IDs (data-quality gap) */}
            {result.no_supplier_property_ids && result.no_supplier_property_ids.length > 0 && (
              <div className="rounded-md border px-3 py-2 space-y-1">
                <div className="text-xs font-medium text-orange-700 dark:text-orange-400">
                  Properties missing supplier ({result.no_supplier_property_ids.length})
                </div>
                <p className="text-xs text-muted-foreground font-mono">
                  {result.no_supplier_property_ids.join(', ')}
                </p>
              </div>
            )}

            {/* Per-booking detail tables */}
            <div className="space-y-2">
              {BUCKET_ORDER.map((k) => (
                result.details[k] && <BookingTable key={k} bucketKey={k} rows={result.details[k]} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
